import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { DebounceInput } from 'react-debounce-input';
import injectSheet from 'react-jss';

import { validateBooleanQuery } from '~/api/search';
import { evisortBlue } from '~/assets/shared-styles/general';
import { ENTER, SPACE } from '~/common/constants/keyCodes';
import EcBooleanTextSearchPopup from '~/components/Shared/EcBooleanTextSearchPopup/EcBooleanTextSearchPopup';
import EcCard from '~/components/Shared/EcCard/EcCard';
import EcFiltersSection from '~/components/Shared/EcFiltersSection/EcFiltersSection';
import SearchIcon from '~/components/Shared/Icons/SearchIcon';
import { Button } from '~/eds/src/components/Button/Button';
import { FormattedMessage, useIntl } from 'react-intl';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Text from '~/ui/components/Text/Text';

import AnalyzerAddFilter from '../AnalyzerAddFilter/AnalyzerAddFilter';
import styles from './AnalyzerForm.styles';

const AnalyzerForm = ({
  classes,
  filters,
  filterOptions,
  updateFilters,
  updateFilterOptions,
  booleanQuery,
  updateBooleanQuery,
  provisionOptions,
  handleSubmit,
}) => {
  const intl = useIntl();
  const [showSyntaxPopup, setShowSyntaxPopup] = useState(false);
  const [showSection, setShowSection] = useState(false);
  const [booleanQueryError, setBooleanQueryError] = useState(null);

  const handleFilterOptionsChange = (filterId) => {
    updateFilterOptions(filterId);
  };

  const handleBooleanInputChange = (e) => {
    const query = e.target.value;

    updateBooleanQuery(query);
    validateBooleanQuery(query)
      .then(() => setBooleanQueryError(null))
      .catch((err) => setBooleanQueryError(err?.response?.data?.detail));
  };

  const handleOnAddSectionKeypress = (e) => {
    if (e.keyCode === ENTER || e.keyCode === SPACE) setShowSection(true);
  };

  const handleOnAddSectionClick = () => {
    setShowSection(true);
  };

  const renderSearchInputAppendix = () => {
    let appendixContent = null;
    if (booleanQueryError) {
      appendixContent = (
        <Text color="red-400" variant="xs-dense-medium">
          {booleanQueryError}
        </Text>
      );
    } else if (booleanQuery.length) {
      appendixContent = (
        <FlexLayout justifyContent="flex-end">
          <Text color="gray-600" variant="xs-dense">
            <FormattedMessage
              id="AnalyzerForm.pressReturnToSearch"
              defaultMessage="Press <strong>Return</strong> to run search."
              description="Instruction text for running search"
              values={{ strong: (chunks) => <strong>{chunks}</strong> }}
            />
          </Text>
        </FlexLayout>
      );
    }

    return <div className={classes.searchInputAppendix}>{appendixContent}</div>;
  };

  const renderAnalyzerBooleanSearch = () => {
    return (
      <FlexLayout flexDirection="column" space={4}>
        <FlexLayout flexDirection="column" space={2}>
          <form className={classes.analyzerForm} onSubmit={handleSubmit}>
            <FlexLayout
              alignItems="center"
              className={
                booleanQueryError
                  ? classes.errorSearchInputWrapper
                  : classes.searchInputWrapper
              }
              space={2}
            >
              <SearchIcon color={evisortBlue} opacity="1" />
              <DebounceInput
                debounceTimeout={300}
                placeholder={intl.formatMessage({
                  id: 'AnalyzerForm.searchPlaceholder',
                  defaultMessage: 'Search filenames and content',
                  description: 'Placeholder text for search input',
                })}
                className={classes.searchInput}
                value={booleanQuery}
                onChange={handleBooleanInputChange}
              />
              <Button
                icon="help"
                iconPosition="left"
                text={intl.formatMessage({
                  id: 'AnalyzerForm.helpButton',
                  defaultMessage: 'Help',
                  description: 'Help button text',
                })}
                variant="action"
                onClick={() => setShowSyntaxPopup(true)}
              />
            </FlexLayout>
          </form>
          {renderSearchInputAppendix()}
        </FlexLayout>
        <FlexLayout
          alignItems="center"
          flexDirection="column"
          className={classes.booleanSearchPlaceholder}
          space={2}
        >
          <Text color="gray-700" variant="l-dense-bold">
            <FormattedMessage
              id="AnalyzerForm.orAddFiltersText"
              defaultMessage="or add filters to run a new Analyzer search."
              description="Text encouraging users to add filters"
            />
          </Text>
          <Text color="gray-700" variant="m-dense-bold">
            <FormattedMessage
              id="AnalyzerForm.sidebarInstructions"
              defaultMessage="<emoji>👈</emoji> Use the sidebar on the left-hand side to <addFilterLink>add a filter</addFilterLink> or <addSectionLink>add a section</addSectionLink>."
              description="Instructions for using the sidebar to add filters or sections"
              values={{
                emoji: (chunks) => (
                  <span role="img" aria-label=":point_left:">
                    {chunks}
                  </span>
                ),
                addFilterLink: (chunks) => (
                  <AnalyzerAddFilter
                    render={({ onClick, onKeyDown }) => (
                      <span
                        role="button"
                        tabIndex="0"
                        id="analyzer-add-a-filter-link"
                        className={classes.addSectionButton}
                        onClick={onClick}
                        onKeyDown={onKeyDown}
                      >
                        {chunks}
                      </span>
                    )}
                    filters={filters}
                    filterOptions={filterOptions}
                    updateFilters={updateFilters}
                    updateFilterOptions={updateFilterOptions}
                    provisionOptions={provisionOptions}
                  />
                ),
                addSectionLink: (chunks) => (
                  <span
                    role="button"
                    tabIndex="0"
                    id="analyzer-add-a-section-link"
                    className={classes.addSectionButton}
                    onClick={handleOnAddSectionClick}
                    onKeyDown={handleOnAddSectionKeypress}
                  >
                    {chunks}
                  </span>
                ),
              }}
            />
          </Text>
        </FlexLayout>
      </FlexLayout>
    );
  };

  const renderAnalyzerFormContainer = () => {
    return (
      <FlexLayout flexDirection="column" space={4}>
        <FlexLayout
          alignItems="center"
          className={classes.sectionHeader}
          space={3}
        >
          <SearchIcon size="32" color={evisortBlue} opacity="1" />
          <FlexLayout flexDirection="column" space={1}>
            <Text color="gray-700" variant="l-dense-bold">
              <FormattedMessage
                id="AnalyzerForm.runNewSearchTitle"
                defaultMessage="Run a New Search"
                description="Section title for running a new search"
              />
            </Text>
            <Text color="gray-700" variant="m-spaced">
              <FormattedMessage
                id="AnalyzerForm.addFiltersDescription"
                defaultMessage="Add filters and run a custom search."
                description="Description text for adding filters"
              />
            </Text>
          </FlexLayout>
        </FlexLayout>
        <FlexLayout flexDirection="column" space={4}>
          <EcFiltersSection
            filters={filters}
            filterOptions={filterOptions}
            provisionOptions={provisionOptions}
            onChange={(selectedFilters) => updateFilters(selectedFilters)}
            handleFilterOptionsChange={handleFilterOptionsChange}
            openEmptySection={showSection}
            rootSection
          />
          <FlexLayout
            id="analyzerFormActions"
            alignItems="center"
            justifyContent="flex-end"
            space={4}
          >
            <Button
              id="analyzer_buttonActions_resetSearchButton"
              text={intl.formatMessage({
                id: 'AnalyzerForm.resetButton',
                defaultMessage: 'Reset',
                description: 'Reset button text',
              })}
              onClick={() => updateFilters([])}
            />
            <Button
              disabled={filters[0]?.error}
              id="analyzer_buttonActions_runSearchButton"
              variant="primary"
              text={intl.formatMessage({
                id: 'AnalyzerForm.runSearchButton',
                defaultMessage: 'Run Search',
                description: 'Run search button text',
              })}
              onClick={handleSubmit}
            />
          </FlexLayout>
        </FlexLayout>
      </FlexLayout>
    );
  };

  return (
    <>
      <EcCard contentStyles={{ padding: '16px' }}>
        {filters.length || showSection
          ? renderAnalyzerFormContainer()
          : renderAnalyzerBooleanSearch()}
      </EcCard>
      {showSyntaxPopup && (
        <EcBooleanTextSearchPopup
          handleOnCloseClick={() => setShowSyntaxPopup(false)}
        />
      )}
    </>
  );
};

AnalyzerForm.propTypes = {
  classes: PropTypes.object.isRequired,
  booleanQuery: PropTypes.string.isRequired,
  updateBooleanQuery: PropTypes.func.isRequired,
  filters: PropTypes.array.isRequired,
  filterOptions: PropTypes.array.isRequired,
  provisionOptions: PropTypes.array.isRequired,
  updateFilters: PropTypes.func.isRequired,
  updateFilterOptions: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
};

export default injectSheet(styles)(AnalyzerForm);
